import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useSystemPlans, useApplyPlan } from '@/hooks/use-plans';
import { parseFeatures } from '@/lib/plans/enforce';
import { Loader2, CheckCircle, Star, Zap } from 'lucide-react';

interface PlanManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  systemCode: string;
  systemName: string;
  currentPlanId?: string;
  onSuccess?: () => void;
}

export function PlanManagementModal({ 
  open, 
  onOpenChange, 
  companyId, 
  systemCode, 
  systemName,
  currentPlanId,
  onSuccess 
}: PlanManagementModalProps) {
  const { toast } = useToast();
  const [selectedPlanId, setSelectedPlanId] = useState('');
  
  const { data: systemPlans = [], isLoading: loadingPlans } = useSystemPlans(systemCode);
  const applyPlanMutation = useApplyPlan();

  const handleApplyPlan = async () => {
    if (!selectedPlanId) {
      toast({
        title: 'Erro',
        description: 'Selecione um plano para continuar',
        variant: 'destructive',
      });
      return;
    }

    try {
      await applyPlanMutation.mutateAsync({
        companyId: companyId,
        systemCode: systemCode,
        planId: selectedPlanId,
        action: currentPlanId ? 'upgrade' : 'activate',
        reason: currentPlanId ? 'Upgrade via admin interface' : 'Initial plan activation'
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const formatPrice = (monthly: number, yearly: number) => {
    return `R$ ${monthly.toFixed(2)}/mês (R$ ${yearly.toFixed(2)}/ano)`;
  };

  const renderFeatures = (features: any) => {
    const parsed = parseFeatures(features);
    const items: string[] = [];

    // Apenas veículos para FleetONE
    const vehiclesMax = (parsed.limits as any).fleet_vehicles_max || (parsed.limits as any).vehicles_max;
    if (vehiclesMax) {
      items.push(`Até ${vehiclesMax} veículos`);
    }

    return items;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Zap className="h-5 w-5 text-primary" />
            <span>Gerenciar Plano - {systemName}</span>
          </DialogTitle>
          <DialogDescription>
            {currentPlanId ? 'Alterar o plano atual' : 'Selecionar um plano para ativar'}
          </DialogDescription>
        </DialogHeader>

        {loadingPlans ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Carregando planos...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <RadioGroup value={selectedPlanId} onValueChange={setSelectedPlanId}>
              <div className="grid gap-4">
                {systemPlans.map((plan) => {
                  const features = renderFeatures(plan.features);
                  const isPopular = plan.name.toLowerCase().includes('pro');
                  
                  return (
                    <div key={plan.id} className="relative">
                      {isPopular && (
                        <div className="absolute -top-2 left-4 z-10">
                          <Badge className="bg-primary text-primary-foreground">
                            <Star className="h-3 w-3 mr-1" />
                            Popular
                          </Badge>
                        </div>
                      )}
                      
                      <Card className={`cursor-pointer transition-all ${
                        selectedPlanId === plan.id 
                          ? 'ring-2 ring-primary shadow-md' 
                          : 'hover:shadow-sm'
                      }`}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <RadioGroupItem 
                                value={plan.id} 
                                id={plan.id}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <Label htmlFor={plan.id} className="cursor-pointer">
                                  <CardTitle className="text-lg flex items-center space-x-2">
                                    <span>{plan.name}</span>
                                    {currentPlanId === plan.id && (
                                      <Badge variant="outline" className="text-xs">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Atual
                                      </Badge>
                                    )}
                                  </CardTitle>
                                </Label>
                                {plan.description && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {plan.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-lg">
                                R$ {plan.price_monthly.toFixed(2)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                /mês
                              </div>
                              <div className="text-xs text-muted-foreground">
                                R$ {plan.price_yearly.toFixed(2)}/ano
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        
                        <CardContent className="pt-0">
                          <div className="space-y-2">
                            {features.map((feature, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </RadioGroup>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleApplyPlan}
            disabled={!selectedPlanId || applyPlanMutation.isPending}
          >
            {applyPlanMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {currentPlanId ? 'Alterar Plano' : 'Ativar Plano'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}